export type BoardTarget = 'self' | 'left' | 'right';

export type MissionType =
  | 'min_combo_rank'
  | 'exact_combo_rank'
  | 'exact_combo'
  | 'sum_min'
  | 'sum_max'
  | 'value_count_min'
  | 'parity_count_min'
  | 'range_count_min'
  | 'unique_face_count_min'
  | 'contains_all_values'
  | 'consecutive_count_min'
  | 'exact_dice_set'
  | 'compare_hand_higher'
  | 'compare_hand_lower'
  | 'compare_targets_hand'
  | 'compare_sum_higher'
  | 'compare_sum_lower'
  | 'compare_targets_sum'
  | 'same_combo_name'
  | 'different_combo_name'
  | 'same_combo_compare_hand'
  | 'between_neighbors'
  | 'sum_diff_max'
  | 'hand_higher_sum_lower'
  | 'hand_lower_sum_higher'
  | 'compare_targets_hand_sum_cross'
  | 'target_hand_extreme'
  | 'neighbors_same_combo'
  | 'neighbors_different_combo'
  | 'neighbors_sum_diff'
  | 'combo_asymmetric_neighbors'
  | 'last_die_in_combo'
  | 'last_die_is_max';

export interface MissionParams {
  target?: BoardTarget;
  rank?: number;
  combo?: string;
  value?: number;
  count?: number;
  values?: number[];
  parity?: 'even' | 'odd';
  neighbor?: 'left' | 'right';
  higher?: BoardTarget;
  lower?: BoardTarget;
  handHigher?: BoardTarget;
  handLower?: BoardTarget;
  sumHigher?: BoardTarget;
  sumLower?: BoardTarget;
  diff?: number;
  mode?: 'max' | 'min';
  dice?: number[];
  extreme?: 'highest' | 'lowest';
  strong?: BoardTarget;
  weak?: BoardTarget;
  minRank?: number;
}

export interface MissionConfig {
  id: string;
  name: string;
  description: string;
  payout: number;
  type: MissionType;
  params: MissionParams;
}

export const MISSIONS: MissionConfig[] = [
  // A. 오른쪽 패 직접 구성 (22)
  {
    id: 'R-C01',
    name: '오른쪽 첫 조합',
    description: '오른쪽 플레이어의 패가 1페어 이상',
    payout: 1,
    type: 'min_combo_rank',
    params: { target: 'right', rank: 7 },
  },
  {
    id: 'R-C02',
    name: '오른쪽 한 쌍',
    description: '오른쪽 플레이어의 패가 정확히 1페어',
    payout: 2,
    type: 'exact_combo_rank',
    params: { target: 'right', rank: 7 },
  },
  {
    id: 'R-C03',
    name: '오른쪽 두 쌍',
    description: '오른쪽 플레이어의 패가 2페어 이상',
    payout: 2,
    type: 'min_combo_rank',
    params: { target: 'right', rank: 6 },
  },
  {
    id: 'R-C04',
    name: '오른쪽 정확한 두 쌍',
    description: '오른쪽 플레이어의 패가 정확히 2페어',
    payout: 3,
    type: 'exact_combo_rank',
    params: { target: 'right', rank: 6 },
  },
  {
    id: 'R-C05',
    name: '오른쪽 세 개',
    description: '오른쪽 플레이어의 패가 3다이스 이상',
    payout: 3,
    type: 'min_combo_rank',
    params: { target: 'right', rank: 5 },
  },
  {
    id: 'R-C06',
    name: '오른쪽 정확한 세 개',
    description: '오른쪽 플레이어의 패가 정확히 3다이스',
    payout: 4,
    type: 'exact_combo_rank',
    params: { target: 'right', rank: 5 },
  },
  {
    id: 'R-C07',
    name: '오른쪽 집과 쌍',
    description: '오른쪽 플레이어의 패가 풀하우스',
    payout: 4,
    type: 'exact_combo',
    params: { target: 'right', combo: 'full_house' },
  },
  {
    id: 'R-C08',
    name: '오른쪽 네 개',
    description: '오른쪽 플레이어의 패가 4다이스 이상',
    payout: 4,
    type: 'min_combo_rank',
    params: { target: 'right', rank: 2 },
  },
  {
    id: 'R-C09',
    name: '오른쪽 연속',
    description: '오른쪽 플레이어의 패가 스트레이트',
    payout: 3,
    type: 'exact_combo',
    params: { target: 'right', combo: 'straight' },
  },
  {
    id: 'R-C10',
    name: '오른쪽 작은 연속',
    description: '오른쪽 플레이어의 패가 정확히 1-2-3-4-5',
    payout: 4,
    type: 'exact_dice_set',
    params: { target: 'right', dice: [1, 2, 3, 4, 5] },
  },
  {
    id: 'R-C11',
    name: '오른쪽 큰 연속',
    description: '오른쪽 플레이어의 패가 정확히 2-3-4-5-6',
    payout: 4,
    type: 'exact_dice_set',
    params: { target: 'right', dice: [2, 3, 4, 5, 6] },
  },
  {
    id: 'R-C12',
    name: '오른쪽 흩어진 패',
    description: '오른쪽 플레이어의 패가 하이 다이스 상태',
    payout: 3,
    type: 'exact_combo_rank',
    params: { target: 'right', rank: 8 },
  },
  {
    id: 'R-C13',
    name: '오른쪽 높은 눈',
    description: '오른쪽 플레이어의 눈 합계가 20 이상',
    payout: 2,
    type: 'sum_min',
    params: { target: 'right', count: 20 },
  },
  {
    id: 'R-C14',
    name: '오른쪽 낮은 눈',
    description: '오른쪽 플레이어의 눈 합계가 15 이하',
    payout: 2,
    type: 'sum_max',
    params: { target: 'right', count: 15 },
  },
  {
    id: 'R-C15',
    name: '오른쪽 여섯 쌍',
    description: '오른쪽 플레이어의 패에 6이 2개 이상',
    payout: 2,
    type: 'value_count_min',
    params: { target: 'right', value: 6, count: 2 },
  },
  {
    id: 'R-C16',
    name: '오른쪽 하나 쌍',
    description: '오른쪽 플레이어의 패에 1이 2개 이상',
    payout: 2,
    type: 'value_count_min',
    params: { target: 'right', value: 1, count: 2 },
  },
  {
    id: 'R-C17',
    name: '오른쪽 짝수 집중',
    description: '오른쪽 플레이어의 패에 짝수가 4개 이상',
    payout: 3,
    type: 'parity_count_min',
    params: { target: 'right', parity: 'even', count: 4 },
  },
  {
    id: 'R-C18',
    name: '오른쪽 홀수 집중',
    description: '오른쪽 플레이어의 패에 홀수가 4개 이상',
    payout: 3,
    type: 'parity_count_min',
    params: { target: 'right', parity: 'odd', count: 4 },
  },
  {
    id: 'R-C19',
    name: '오른쪽 높은 숫자 집중',
    description: '오른쪽 플레이어의 패에 4, 5, 6이 총 4개 이상',
    payout: 3,
    type: 'range_count_min',
    params: { target: 'right', values: [4, 5, 6], count: 4 },
  },
  {
    id: 'R-C20',
    name: '오른쪽 낮은 숫자 집중',
    description: '오른쪽 플레이어의 패에 1, 2, 3이 총 4개 이상',
    payout: 3,
    type: 'range_count_min',
    params: { target: 'right', values: [1, 2, 3], count: 4 },
  },
  {
    id: 'R-C21',
    name: '오른쪽 다양한 눈',
    description: '오른쪽 플레이어의 패에 서로 다른 눈이 4종류 이상',
    payout: 1,
    type: 'unique_face_count_min',
    params: { target: 'right', count: 4 },
  },
  {
    id: 'R-C22',
    name: '오른쪽 양끝 패',
    description: '오른쪽 플레이어의 패에 1과 6이 모두 포함',
    payout: 2,
    type: 'contains_all_values',
    params: { target: 'right', values: [1, 6] },
  },
  // B. 오른쪽 패 비교·관계 (6)
  {
    id: 'R-V01',
    name: '오른쪽 우위',
    description: '오른쪽 플레이어의 패가 내 패보다 높음',
    payout: 2,
    type: 'compare_targets_hand',
    params: { higher: 'right', lower: 'self' },
  },
  {
    id: 'R-V02',
    name: '오른쪽 열세',
    description: '오른쪽 플레이어의 패가 내 패보다 낮음',
    payout: 2,
    type: 'compare_targets_hand',
    params: { higher: 'self', lower: 'right' },
  },
  {
    id: 'R-V03',
    name: '오른쪽 높은 합계',
    description: '오른쪽 플레이어의 눈 합계가 내 눈 합계보다 높음',
    payout: 2,
    type: 'compare_targets_sum',
    params: { higher: 'right', lower: 'self' },
  },
  {
    id: 'R-V04',
    name: '오른쪽 낮은 합계',
    description: '오른쪽 플레이어의 눈 합계가 내 눈 합계보다 낮음',
    payout: 2,
    type: 'compare_targets_sum',
    params: { higher: 'self', lower: 'right' },
  },
  {
    id: 'R-V05',
    name: '오른쪽이 왼쪽보다 높음',
    description: '오른쪽 플레이어의 패가 왼쪽 플레이어의 패보다 높음',
    payout: 3,
    type: 'compare_targets_hand',
    params: { higher: 'right', lower: 'left' },
  },
  {
    id: 'R-V06',
    name: '오른쪽이 왼쪽보다 낮음',
    description: '오른쪽 플레이어의 패가 왼쪽 플레이어의 패보다 낮음',
    payout: 3,
    type: 'compare_targets_hand',
    params: { higher: 'left', lower: 'right' },
  },
  // C. 오른쪽 패 전체 구도 (2)
  {
    id: 'R-O01',
    name: '오른쪽 최고 패',
    description: '오른쪽 플레이어의 패가 전체 플레이어 중 가장 높음',
    payout: 3,
    type: 'target_hand_extreme',
    params: { target: 'right', extreme: 'highest' },
  },
  {
    id: 'R-O02',
    name: '오른쪽 최저 패',
    description: '오른쪽 플레이어의 패가 전체 플레이어 중 가장 낮음',
    payout: 3,
    type: 'target_hand_extreme',
    params: { target: 'right', extreme: 'lowest' },
  },
  // D. 내 패 간접 구성 (10)
  {
    id: 'M-C01',
    name: '내 첫 조합',
    description: '내 패가 1페어 이상',
    payout: 2,
    type: 'min_combo_rank',
    params: { rank: 7 },
  },
  {
    id: 'M-C02',
    name: '내 한 쌍',
    description: '내 패가 정확히 1페어',
    payout: 3,
    type: 'exact_combo_rank',
    params: { rank: 7 },
  },
  {
    id: 'M-C03',
    name: '내 두 쌍',
    description: '내 패가 2페어 이상',
    payout: 4,
    type: 'min_combo_rank',
    params: { rank: 6 },
  },
  {
    id: 'M-C04',
    name: '내 세 개',
    description: '내 패가 3다이스 이상',
    payout: 5,
    type: 'min_combo_rank',
    params: { rank: 5 },
  },
  {
    id: 'M-C05',
    name: '내 흩어진 패',
    description: '내 패가 하이 다이스 상태',
    payout: 5,
    type: 'exact_combo_rank',
    params: { rank: 8 },
  },
  {
    id: 'M-C06',
    name: '내 연속 후보',
    description: '내 패에 연속 숫자 4개 이상 포함',
    payout: 4,
    type: 'consecutive_count_min',
    params: { count: 4 },
  },
  {
    id: 'M-C07',
    name: '내 높은 눈',
    description: '내 눈 합계가 20 이상',
    payout: 3,
    type: 'sum_min',
    params: { count: 20 },
  },
  {
    id: 'M-C08',
    name: '내 낮은 눈',
    description: '내 눈 합계가 15 이하',
    payout: 3,
    type: 'sum_max',
    params: { count: 15 },
  },
  {
    id: 'M-C09',
    name: '내 여섯 쌍',
    description: '내 패에 6이 2개 이상',
    payout: 4,
    type: 'value_count_min',
    params: { value: 6, count: 2 },
  },
  {
    id: 'M-C10',
    name: '내 하나 쌍',
    description: '내 패에 1이 2개 이상',
    payout: 4,
    type: 'value_count_min',
    params: { value: 1, count: 2 },
  },
  // E. 내 패 비교·관계 (8)
  {
    id: 'M-V01',
    name: '낮은 합계의 승리',
    description: '내 패가 오른쪽 플레이어의 패보다 높고, 내 눈 합계는 오른쪽보다 낮음',
    payout: 5,
    type: 'hand_higher_sum_lower',
    params: { neighbor: 'right' },
  },
  {
    id: 'M-V02',
    name: '높은 합계의 패배',
    description: '내 패가 오른쪽 플레이어의 패보다 낮고, 내 눈 합계는 오른쪽보다 높음',
    payout: 5,
    type: 'hand_lower_sum_higher',
    params: { neighbor: 'right' },
  },
  {
    id: 'M-V03',
    name: '같은 조합, 내가 우위',
    description: '내 패와 오른쪽 플레이어의 조합명이 같고, 내 패가 더 높음',
    payout: 5,
    type: 'same_combo_compare_hand',
    params: { higher: 'self', lower: 'right' },
  },
  {
    id: 'M-V04',
    name: '같은 조합, 오른쪽 우위',
    description: '내 패와 오른쪽 플레이어의 조합명이 같고, 오른쪽 플레이어의 패가 더 높음',
    payout: 4,
    type: 'same_combo_compare_hand',
    params: { higher: 'right', lower: 'self' },
  },
  {
    id: 'M-V05',
    name: '오른쪽과 동급',
    description: '내 패와 오른쪽 플레이어의 조합명이 같음',
    payout: 3,
    type: 'same_combo_name',
    params: { neighbor: 'right' },
  },
  {
    id: 'M-V06',
    name: '오른쪽과 다른 조합',
    description: '내 패와 오른쪽 플레이어의 조합명이 다름',
    payout: 1,
    type: 'different_combo_name',
    params: { neighbor: 'right' },
  },
  {
    id: 'M-V07',
    name: '내 패가 중간',
    description: '내 패가 왼쪽과 오른쪽 중 한 명보다 높고 다른 한 명보다 낮음',
    payout: 5,
    type: 'between_neighbors',
    params: {},
  },
  {
    id: 'M-V08',
    name: '오른쪽과 비슷한 합계',
    description: '내 눈 합계와 오른쪽 플레이어의 눈 합계 차이가 3 이하',
    payout: 2,
    type: 'sum_diff_max',
    params: { neighbor: 'right', diff: 3 },
  },
  // F. 내 패 마지막 주사위 (2)
  {
    id: 'M-L01',
    name: '마지막 한 수',
    description: '내가 마지막에 남긴 주사위가 내 패의 조합에 포함됨',
    payout: 3,
    type: 'last_die_in_combo',
    params: {},
  },
  {
    id: 'M-L02',
    name: '마지막 높은 눈',
    description: '내가 마지막에 남긴 주사위가 내 패의 가장 높은 눈',
    payout: 2,
    type: 'last_die_is_max',
    params: {},
  },
  // G. 왼쪽 관련 비교·관계 (10)
  {
    id: 'L-V01',
    name: '왼쪽의 낮은 합계 승리',
    description: '왼쪽 플레이어의 패가 오른쪽보다 높고, 왼쪽 플레이어의 눈 합계는 오른쪽보다 낮음',
    payout: 5,
    type: 'compare_targets_hand_sum_cross',
    params: { handHigher: 'left', handLower: 'right', sumHigher: 'right', sumLower: 'left' },
  },
  {
    id: 'L-V02',
    name: '왼쪽의 높은 합계 패배',
    description: '왼쪽 플레이어의 패가 오른쪽보다 낮고, 왼쪽 플레이어의 눈 합계는 오른쪽보다 높음',
    payout: 5,
    type: 'compare_targets_hand_sum_cross',
    params: { handHigher: 'right', handLower: 'left', sumHigher: 'left', sumLower: 'right' },
  },
  {
    id: 'L-V03',
    name: '왼쪽 높은 합계',
    description: '왼쪽 플레이어의 눈 합계가 오른쪽 플레이어보다 높음',
    payout: 2,
    type: 'compare_targets_sum',
    params: { higher: 'left', lower: 'right' },
  },
  {
    id: 'L-V04',
    name: '왼쪽 낮은 합계',
    description: '왼쪽 플레이어의 눈 합계가 오른쪽 플레이어보다 낮음',
    payout: 2,
    type: 'compare_targets_sum',
    params: { higher: 'right', lower: 'left' },
  },
  {
    id: 'L-V05',
    name: '양옆 동급',
    description: '왼쪽 플레이어와 오른쪽 플레이어의 조합명이 같음',
    payout: 5,
    type: 'neighbors_same_combo',
    params: {},
  },
  {
    id: 'L-V06',
    name: '양옆 다른 조합',
    description: '왼쪽 플레이어와 오른쪽 플레이어의 조합명이 다름',
    payout: 1,
    type: 'neighbors_different_combo',
    params: {},
  },
  {
    id: 'L-V07',
    name: '양옆 비슷한 합계',
    description: '왼쪽과 오른쪽 플레이어의 눈 합계 차이가 3 이하',
    payout: 2,
    type: 'neighbors_sum_diff',
    params: { diff: 3, mode: 'max' },
  },
  {
    id: 'L-V08',
    name: '양옆 큰 차이',
    description: '왼쪽과 오른쪽 플레이어의 눈 합계 차이가 7 이상',
    payout: 4,
    type: 'neighbors_sum_diff',
    params: { diff: 7, mode: 'min' },
  },
  {
    id: 'L-V09',
    name: '오른쪽만 조합 보유',
    description: '오른쪽 플레이어는 1페어 이상이고 왼쪽 플레이어는 하이 다이스 상태',
    payout: 5,
    type: 'combo_asymmetric_neighbors',
    params: { strong: 'right', weak: 'left', minRank: 7 },
  },
  {
    id: 'L-V10',
    name: '왼쪽만 조합 보유',
    description: '왼쪽 플레이어는 1페어 이상이고 오른쪽 플레이어는 하이 다이스 상태',
    payout: 5,
    type: 'combo_asymmetric_neighbors',
    params: { strong: 'left', weak: 'right', minRank: 7 },
  },
];

export const MISSION_BY_ID: Record<string, MissionConfig> = Object.fromEntries(
  MISSIONS.map((m) => [m.id, m]),
);

export const MISSION_IDS: string[] = MISSIONS.map((m) => m.id);
